import {
  Badge,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'

export type ResetWorkflowPreviewTask = {
  id: number
  title: string
  status_label?: string | null
  reason?: string
}

export type ResetWorkflowPreview = {
  safe: ResetWorkflowPreviewTask[]
  active: ResetWorkflowPreviewTask[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  preview: ResetWorkflowPreview | null
  loadingPreview?: boolean
  submitting?: boolean
  onRemoveSafeOnly: () => void
  onRemoveAll: () => void
}

function TaskRow({
  task,
  variant,
}: {
  task: ResetWorkflowPreviewTask
  variant: 'safe' | 'live'
}) {
  const colors = useThemeColors()
  const isLive = variant === 'live'

  return (
    <Box
      px={3}
      py={2.5}
      borderRadius="md"
      borderWidth="1px"
      borderColor={isLive ? 'orange.200' : colors.borderLight}
      bg={isLive ? 'orange.50' : colors.inputBg ?? 'gray.50'}
      borderLeftWidth="3px"
      borderLeftColor={isLive ? 'orange.400' : 'green.400'}
    >
      <HStack justify="space-between" align="flex-start" spacing={3}>
        <Box minW={0}>
          <Text fontSize="sm" fontWeight="600" noOfLines={2}>
            {task.title}
          </Text>
          <HStack spacing={2} mt={0.5} flexWrap="wrap">
            {task.status_label ? (
              <Text fontSize="xs" color={colors.textSecondary}>
                {task.status_label}
              </Text>
            ) : null}
            {task.reason ? (
              <Badge colorScheme={isLive ? 'orange' : 'gray'} fontSize="2xs">
                {task.reason}
              </Badge>
            ) : null}
          </HStack>
        </Box>
      </HStack>
    </Box>
  )
}

function GroupLabel({ children }: { children: string }) {
  const colors = useThemeColors()
  return (
    <Text
      fontSize="xs"
      fontWeight="700"
      color={colors.textMuted}
      textTransform="uppercase"
      letterSpacing="wider"
      mb={2}
    >
      {children}
    </Text>
  )
}

export default function ResetWorkflowModal({
  isOpen,
  onClose,
  preview,
  loadingPreview = false,
  submitting = false,
  onRemoveSafeOnly,
  onRemoveAll,
}: Props) {
  const colors = useThemeColors()
  const safeCount = preview?.safe.length ?? 0
  const activeCount = preview?.active.length ?? 0
  const totalCount = safeCount + activeCount
  const hasActive = activeCount > 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reset workflow?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loadingPreview || !preview ? (
            <HStack py={8} justify="center">
              <Spinner size="sm" />
              <Text fontSize="sm" color={colors.textSecondary}>
                Checking which tasks are safe to remove…
              </Text>
            </HStack>
          ) : totalCount === 0 ? (
            <Text fontSize="sm" color={colors.textSecondary}>
              No tasks to remove.
            </Text>
          ) : (
            <VStack align="stretch" spacing={5}>
              <Text fontSize="sm" color={colors.textSecondary}>
                {hasActive
                  ? 'Some tasks have real work in progress. Choose whether to remove only untouched tasks or everything.'
                  : `Remove ${safeCount} task${safeCount === 1 ? '' : 's'}? Task progress will be lost. You can then apply a different template.`}
              </Text>

              {safeCount > 0 ? (
                <Box>
                  <GroupLabel>{`Safe to remove · ${safeCount}`}</GroupLabel>
                  <VStack align="stretch" spacing={2}>
                    {preview.safe.map((task) => (
                      <TaskRow key={task.id} task={task} variant="safe" />
                    ))}
                  </VStack>
                </Box>
              ) : null}

              {hasActive ? (
                <Box>
                  <GroupLabel>{`Active — will interrupt real work · ${activeCount}`}</GroupLabel>
                  <VStack align="stretch" spacing={2}>
                    {preview.active.map((task) => (
                      <TaskRow key={task.id} task={task} variant="live" />
                    ))}
                  </VStack>
                </Box>
              ) : null}

              {safeCount === 0 && hasActive ? (
                <Text fontSize="sm" color={colors.textSecondary}>
                  Every task on this ticket is active. Removing them will interrupt in-progress work.
                </Text>
              ) : null}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          <Button variant="outline" onClick={onClose} isDisabled={submitting}>
            Cancel
          </Button>
          {!loadingPreview && preview && totalCount > 0 ? (
            hasActive ? (
              <>
                {safeCount > 0 ? (
                  <Button
                    variant="solid"
                    colorScheme="gray"
                    onClick={onRemoveSafeOnly}
                    isLoading={submitting}
                  >
                    Remove the {safeCount} safe task{safeCount === 1 ? '' : 's'} only
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  colorScheme="red"
                  onClick={onRemoveAll}
                  isLoading={submitting}
                >
                  Remove all {totalCount} anyway
                </Button>
              </>
            ) : (
              <Button colorScheme="red" onClick={onRemoveSafeOnly} isLoading={submitting}>
                Remove {safeCount} task{safeCount === 1 ? '' : 's'}
              </Button>
            )
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
